import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import BorrowAmount from "./Components/BorrowAmount";
import BorrowInfo from "./Components/BorrowInfo";
import BorrowSubmit from "./Components/BorrowSubmit";
import CoinDrawerSelect from "components/CoinDrawerSelect";
import { Grid } from "@mui/material";
import { repeat } from "utils/helpers/interval";
import useNotify from "hooks/useNotify";
import useDispatch from "hooks/useDispatch";
import { getBorrow, getBorrowAmountMax } from "store/slices/borrow/borrowSlice";
import { useSelector } from "react-redux";

let getBorrowAmountMaxInterval;

function Borrow({ onClose }) {
  const dispatch = useDispatch()
  const notify = useNotify();

  const borrowInitial = useSelector((state) => state.borrow.borrowInitial?.data)
  const borrowAmountMax = useSelector((state) => state.borrow.borrowAmountMax?.data)

  const [loading, setLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const coinId = +searchParams.get("borrow") || 1;
  const basketId = +searchParams.get("basketId");

  const methods = useForm({
    defaultValues: {
      coinId,
      amount: "",
      basketOwnerId: basketId
    },
  });

  const { reset } = methods;

  const handleGetBorrowAmountMax = async ({ coinId, basketOwnerId: basketId }) => {
    try {
      setLoading(true);

      dispatch(getBorrowAmountMax({ coinId, basketOwnerId: basketId }));
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }
    setLoading(false);
  };

  const selectedCoinIsInLisCoin = borrowInitial?.lisCoin?.find(({ id }) => id === coinId)

  useEffect(() => {
    if (borrowInitial?.validSeconds && coinId && basketId && selectedCoinIsInLisCoin) {
      handleGetBorrowAmountMax({ coinId, basketOwnerId: basketId });

      clearInterval(getBorrowAmountMaxInterval);

      getBorrowAmountMaxInterval = repeat({
        action: () => handleGetBorrowAmountMax(coinId),
        time: borrowInitial?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getBorrowAmountMaxInterval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinId, borrowInitial, selectedCoinIsInLisCoin]);

  const handleChangeCoin = (data) => {
    const allSearchParams = Object.fromEntries([...searchParams]);
    setSearchParams({ ...allSearchParams, borrow: data?.id });
  };

  const selectedCoin = useMemo(
    () => {
      const allSearchParams = Object.fromEntries([...searchParams]);
      const selected = borrowInitial?.lisCoin?.find((coin) => coin?.id === coinId)

      if (!selected) {
        const firstOption = borrowInitial?.lisCoin?.[0]

        setSearchParams({ ...allSearchParams, borrow: firstOption?.id });
      }

      return selected
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [borrowInitial, coinId]
  );

  useEffect(() => {
    dispatch(getBorrow({ coinId, basketOwnerId: basketId }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reset((formValues) => ({
      ...formValues,
      amount: "",
      coinId,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinId, reset]);

  return (
    <FormProvider {...methods}>
      <form>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CoinDrawerSelect
              coinList={borrowInitial}
              onCoinClick={handleChangeCoin}
              selectedCoin={selectedCoin}
            />
          </Grid>

          <BorrowAmount
            loading={loading}
            borrowAmountMax={borrowAmountMax}
            selectedCoin={selectedCoin}
          />

          <BorrowInfo
            borrowAmountMax={borrowAmountMax}
            selectedCoin={selectedCoin}
          />

          <BorrowSubmit
            onClose={onClose}
            borrowAmountMax={borrowAmountMax}
            selectedCoin={selectedCoin}
          />
        </Grid>
      </form>
    </FormProvider>
  );
}

export default Borrow;
